import { HTMLAttributes } from 'react';
import { useCustomNavigate } from '@/website/hooks/useCustomNavigate';
import { Routes } from '@/website/util/routes';
import { JoveButton } from '@jove-frontend/mui';
import { useJoveBreakpoint } from '@jove-frontend/uikit';
import { ArrowCircleRight, Done } from '@mui/icons-material';
import { Box, Container, Typography } from '@mui/material';
import clsx from 'clsx';

type WhySmartChoiceProps = HTMLAttributes<HTMLDivElement> & { post?: any };

const WhySmartChoice = (props: WhySmartChoiceProps) => {
  const { className, post } = props;
  const { push } = useCustomNavigate();
  const { isMobileLayout } = useJoveBreakpoint();

  const handleSwitchToJove = () => {
    push({ path: Routes.switchToJoveUploadPolicyDocuments() });
  };

  const features = [
    {
      title: post?.smartChoiceFeature1Title,
      description: post?.smartChoiceFeature1Description,
    },
    {
      title: post?.smartChoiceFeature2Title,
      description: post?.smartChoiceFeature2Description,
    },
    {
      title: post?.smartChoiceFeature3Title,
      description: post?.smartChoiceFeature3Description,
    },
    {
      title: post?.smartChoiceFeature4Title,
      description: post?.smartChoiceFeature4Description,
    },
  ];

  return (
    <Box className={clsx('bg-grey-5 w-full py-16', className)}>
      <Box component={Container} className="flex flex-col items-center space-y-12">
        {/* Header Section */}
        <Box className="flex flex-col items-center space-y-3">
          <Typography className="text-primary-black font-serif text-[52px] font-black leading-tight">{post?.smartChoiceTitle}</Typography>
          <Typography variant="h6" className="text-primary-black font-medium">
            {post?.smartChoiceSubtitle}
          </Typography>
        </Box>

        <Box className={clsx('flex w-full rounded-[20px] bg-white', isMobileLayout ? 'flex-col gap-6 p-4' : 'flex-row gap-12')}>
          {/* Left Side - Image */}
          <Box className={clsx('flex justify-center', isMobileLayout ? 'w-full' : 'flex-1')}>
            <Box
              component="img"
              src="https://storage.googleapis.com/jove-public-images/website/landing-pages/universal/switch-to-jove-smart-choice-img-1.png"
              alt="Smart Choice"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: '16px',
              }}
            />
          </Box>

          {/* Right Side - Features */}
          <Box className={clsx('flex flex-col justify-center space-y-4 py-5', isMobileLayout ? 'w-full' : 'flex-1')}>
            {features.map((feature, index) => (
              <Box key={index} className="flex flex-row gap-4">
                {/* Icon */}
                <Box className="bg-light-blue flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                  <Done className="text-brand-secondary h-6 w-6" />
                </Box>

                {/* Content */}
                <Box className="flex flex-col space-y-1">
                  <Typography variant="h6" className="text-primary-black font-black">
                    {feature.title}
                  </Typography>
                  <Typography className="text-primary-black text-lg font-medium leading-tight">{feature.description}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Button */}
        <JoveButton size="large" variant="contained" endIcon={<ArrowCircleRight />} onClick={handleSwitchToJove}>
          {post?.smartChoiceButton}
        </JoveButton>
      </Box>
    </Box>
  );
};

export default WhySmartChoice;
